/**
 * useWheelSteering — Auto-steers front wheels based on the active scene.
 *
 * Watches `activeScene` in the app store and calls
 * `engine.setWheelSteeringAngle()` whenever the scene changes.
 * The target angle is looked up from `wheelSteering.scenes[sceneId]`;
 * scenes not listed in the map default to 0° (straight).
 *
 * Only activates when `config.model.wheelSteering` is set.
 * Models without the config block are unaffected.
 *
 * @see packages/engine/src/core/WheelManager.ts
 */
import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type { Engine } from '@ims/engine';
import type { ResolvedConfig } from '@ims/shared';
import { useAppStore } from '../store/app.store';

export function useWheelSteering(
  engineRef: MutableRefObject<Engine | null>,
  config: ResolvedConfig | null,
): void {
  const activeScene = useAppStore((s) => s.activeScene);

  useEffect(() => {
    const engine = engineRef.current;
    const ws = config?.model.wheelSteering;
    if (!engine || !ws) return;

    const angleDeg = ws.scenes[activeScene] ?? 0;
    engine.setWheelSteeringAngle(angleDeg, true);
  }, [activeScene, engineRef, config]);
}
